import { Add, Close } from '@sparkpost/matchbox-icons';
import React, { useEffect } from 'react';
import { ExternalLink } from 'src/components/links';
import { Banner, Button, ScreenReaderOnly, Select, Stack } from 'src/components/matchbox';
import { TranslatableText } from 'src/components/text';
import { LINKS } from 'src/constants';
import { useMultiEntry } from 'src/hooks';
import styled from 'styled-components';
import { COMPARE_BY_OPTIONS } from '../../constants';

const StyledRemoveButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
`;

export function TypeSelect({ id, onChange, options, value }) {
  return (
    <Select
      placeholder="Select Resource"
      placeholderValue="Select Resource"
      id={id}
      label="Type"
      onChange={onChange}
      options={options}
      value={value ? value : 'Select Resource'}
      data-track={true}
    />
  );
}

export function CompareBySelect({ id, hasLikeOptions, onChange, value }) {
  const options = hasLikeOptions
    ? COMPARE_BY_OPTIONS
    : COMPARE_BY_OPTIONS.filter((option) => option.value === 'eq' || option.value === 'notEq');

  return (
    <Select
      label="Compare By"
      labelHidden
      id={id}
      onChange={onChange}
      options={options}
      value={value ? value : options[0].value}
      data-track={true}
    />
  );
}

export function MultiEntryController({
  id,
  initialValueList,
  render,
  setFilterValues,
  filterIndex,
  groupingIndex,
  minLength = 3,
  keycodes = []
}) {
  // Using custom hook within the groupings .map callback wasn't making the linter very happy.
  // Using render props to get around the problem.
  const { value, valueList, error, handleKeyDown, handleChange, handleBlur, handleRemove } =
    useMultiEntry(
      { valueList: initialValueList, minLength },
      { keyCodes: keycodes.length ? keycodes : undefined }
    );

  useEffect(() => {
    setFilterValues({
      values: valueList,
      filterIndex,
      groupingIndex
    });
  }, [setFilterValues, valueList, filterIndex, groupingIndex]);

  return render({
    id,
    value,
    valueList,
    error,
    handleKeyDown,
    handleChange,
    handleBlur,
    handleRemove
  });
}

export function AddButton({ children, onClick }) {
  return (
    <Button onClick={onClick} size="small">
      <TranslatableText>{children}</TranslatableText>
      <Button.Icon as={Add} />
    </Button>
  );
}

export function RemoveButton({ onClick }) {
  return (
    <StyledRemoveButton padding="200" variant="minimal" onClick={onClick} size="small">
      <ScreenReaderOnly>Remove Filter</ScreenReaderOnly>
      <Button.Icon as={Close} />
    </StyledRemoveButton>
  );
}

export function DuplicateErrorBanner(props) {
  return (
    <>
      {/* alert role used to inform screen reader users of the presence of the error since it renders before interactive content in the DOM */}
      {/* negative tabIndex used to allow programmatic focus handling */}
      <div role="alert" tabIndex="-1" data-id={props['data-id']}>
        <Banner size="small" status="danger">
          <Stack space="200">
            <p>
              Duplicate filters are not allowed within a group. See the{' '}
              <ExternalLink to={LINKS.METRICS_GROUPINGS_STRUCTURE_DOCS}>API Docs</ExternalLink> for
              more information.
            </p>
          </Stack>
        </Banner>
      </div>
    </>
  );
}
